import AssignmentIcon from '@mui/icons-material/Assignment';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { Box, CircularProgress, IconButton, InputBase, Tooltip, Typography } from '@mui/material';
import BackendService, { AdminNoteItem } from 'BackendService';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import AdminNoteContent from '../../../components/admin/AdminNoteContent';
import { noteAgo } from './contactOverviewFormatUtils';

const PAGE_SIZE = 20;

export default function ContactNotesSection({ contactExternalId }: { contactExternalId: string }) {
  const [notes, setNotes] = useState<AdminNoteItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [draft, setDraft] = useState('');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bs = useCallback(() => new BackendService(), []);

  useEffect(() => {
    bs()
      .listAdminNotes('CONTACT', contactExternalId, { limit: PAGE_SIZE, offset: 0 })
      .then((res) => {
        const data = res?.data ?? [];
        setNotes(data);
        setHasMore(data.length === PAGE_SIZE);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [contactExternalId, bs]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const loadMore = () => {
    setLoadingMore(true);
    bs()
      .listAdminNotes('CONTACT', contactExternalId, { limit: PAGE_SIZE, offset: notes.length })
      .then((res) => {
        const data = res?.data ?? [];
        setNotes((p) => [...p, ...data]);
        setHasMore(data.length === PAGE_SIZE);
      })
      .catch(console.error)
      .finally(() => setLoadingMore(false));
  };

  const handleSave = async () => {
    const content = draft.trim();
    if (!content || saveStatus === 'saving') return;
    setSaveStatus('saving');
    try {
      await bs().createAdminNote({
        modelType: 'CONTACT',
        modelExternalId: contactExternalId,
        content,
      });
      const res = await bs().listAdminNotes('CONTACT', contactExternalId, {
        limit: Math.max(notes.length + 1, PAGE_SIZE),
        offset: 0,
      });
      const data = res?.data ?? [];
      setNotes(data);
      setHasMore(data.length === PAGE_SIZE);
      setDraft('');
      setSaveStatus('saved');
      timerRef.current = setTimeout(() => setSaveStatus('idle'), 2000);
    } catch {
      setSaveStatus('error');
      timerRef.current = setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  const remove = async (externalId: string) => {
    await bs().deleteAdminNote(externalId).catch(console.error);
    setNotes((p) => p.filter((n) => n.externalId !== externalId));
  };

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          px: 1.5,
          pt: 0.75,
          pb: 0.5,
          borderBottom: notes.length > 0 || loading ? '1px solid' : 'none',
          borderColor: 'divider',
        }}
      >
        <InputBase
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              handleSave();
            }
          }}
          placeholder="Add a note about this contact…"
          multiline
          minRows={2}
          maxRows={6}
          fullWidth
          disabled={saveStatus === 'saving'}
          sx={{ fontSize: '0.875rem', alignItems: 'flex-start' }}
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 24,
            pb: 0.25,
          }}
        >
          <Typography
            variant="caption"
            color={
              saveStatus === 'error'
                ? 'error'
                : saveStatus === 'saved'
                  ? 'success.main'
                  : 'text.disabled'
            }
          >
            {saveStatus === 'saving' && (
              <CircularProgress size={10} sx={{ mr: 0.5, verticalAlign: 'middle' }} />
            )}
            {saveStatus === 'saving'
              ? 'Saving…'
              : saveStatus === 'saved'
                ? '✓ Saved'
                : saveStatus === 'error'
                  ? 'Save failed'
                  : ''}
          </Typography>
          <Tooltip title="Save note (Ctrl+Enter)">
            <span>
              <IconButton
                size="small"
                onClick={handleSave}
                disabled={!draft.trim() || saveStatus === 'saving'}
                color="primary"
                aria-label="Save note"
              >
                {saveStatus === 'saving' ? (
                  <CircularProgress size={16} />
                ) : (
                  <SaveIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
      {(loading || notes.length > 0) && (
        <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={1.5}>
              <CircularProgress size={18} />
            </Box>
          ) : (
            notes.map((note) => (
              <Box
                key={note.externalId}
                sx={{
                  display: 'flex',
                  gap: 1,
                  px: 1.5,
                  py: 0.75,
                  alignItems: 'flex-start',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: hasMore ? '1px solid' : 'none' },
                  '&:hover .note-delete': { opacity: 1 },
                }}
              >
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ whiteSpace: 'nowrap', pt: 0.25, minWidth: 52 }}
                  title={new Date(note.createdAt).toLocaleString('en-AU')}
                >
                  {noteAgo(note.createdAt)}
                </Typography>
                <Typography
                  variant="body2"
                  component="div"
                  sx={{ flex: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.5 }}
                >
                  <AdminNoteContent content={note.content} />
                </Typography>
                <IconButton
                  className="note-delete"
                  size="small"
                  onClick={() => remove(note.externalId)}
                  sx={{ opacity: 0, transition: 'opacity 0.15s', p: 0.25, flexShrink: 0 }}
                  title="Delete note"
                >
                  <DeleteIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ))
          )}
          {hasMore && (
            <Box
              component="button"
              onClick={loadMore}
              disabled={loadingMore}
              sx={{
                display: 'block',
                width: '100%',
                px: 1.5,
                py: 0.5,
                border: 'none',
                bgcolor: 'transparent',
                cursor: 'pointer',
                color: 'text.secondary',
                fontSize: '0.75rem',
                textAlign: 'left',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

export { PAGE_SIZE as CONTACT_NOTES_PAGE_SIZE };
